import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiRequest, withApi } from "../utils/api";

// ── Proyectos y cuentas ──────────────────────────────────────────────────────

export const fetchProjects = createAsyncThunk(
    "marketing/fetchProjects",
    withApi((_, { token }) => apiRequest("/marketing/projects", { token }))
);

export const createProject = createAsyncThunk(
    "marketing/createProject",
    withApi(({ name }, { token }) =>
        apiRequest("/marketing/projects", { method: "POST", body: { name }, token })
    )
);

export const renameProject = createAsyncThunk(
    "marketing/renameProject",
    withApi(({ id, name }, { token }) =>
        apiRequest(`/marketing/projects/${id}`, { method: "PATCH", body: { name }, token })
    )
);

export const deleteProject = createAsyncThunk(
    "marketing/deleteProject",
    withApi(async ({ id }, { token }) => {
        await apiRequest(`/marketing/projects/${id}`, { method: "DELETE", token });
        return id;
    })
);

export const fetchPlatforms = createAsyncThunk(
    "marketing/fetchPlatforms",
    withApi((_, { token }) => apiRequest("/marketing/social/platforms", { token }))
);

export const fetchApiHealth = createAsyncThunk(
    "marketing/fetchApiHealth",
    withApi(() => apiRequest("/health"))
);

// Devuelve la URL de autorización de la red social; el componente redirige.
export const getConnectUrl = createAsyncThunk(
    "marketing/getConnectUrl",
    withApi(async ({ platform, projectId }, { token }) => {
        const data = await apiRequest(`/marketing/social/${platform}/connect`, {
            query: { projectId },
            token,
        });
        return data?.url;
    })
);

export const syncAccount = createAsyncThunk(
    "marketing/syncAccount",
    withApi(({ id }, { token }) =>
        apiRequest(`/marketing/accounts/${id}/sync`, { method: "POST", token })
    )
);

export const disconnectAccount = createAsyncThunk(
    "marketing/disconnectAccount",
    withApi(({ id }, { token }) =>
        apiRequest(`/marketing/accounts/${id}`, { method: "DELETE", token })
    )
);

// ── Juegos de Roblox ─────────────────────────────────────────────────────────

export const fetchRobloxGames = createAsyncThunk(
    "marketing/fetchRobloxGames",
    withApi(async ({ projectId }, { token }) => {
        const games = await apiRequest(`/marketing/projects/${projectId}/roblox-games`, { token });
        return { projectId, games: games || [] };
    })
);

export const linkRobloxGame = createAsyncThunk(
    "marketing/linkRobloxGame",
    withApi(({ projectId, place }, { token }) =>
        apiRequest(`/marketing/projects/${projectId}/roblox-games`, {
            method: "POST",
            body: { place },
            token,
        })
    )
);

export const unlinkRobloxGame = createAsyncThunk(
    "marketing/unlinkRobloxGame",
    withApi(({ id }, { token }) =>
        apiRequest(`/marketing/roblox-games/${id}`, { method: "DELETE", token })
    )
);

// ── Publicaciones (desglose por video) ───────────────────────────────────────

const MAX_POST_PAGES = 10;

// Trae los posts del proyecto (paginados por cursor) y las publicaciones
// detectadas que aún no se asignan a un post, en una sola lista por publicación.
export const fetchPublications = createAsyncThunk(
    "marketing/fetchPublications",
    withApi(async ({ projectId }, { token }) => {
        const posts = [];
        let cursor;
        for (let page = 0; page < MAX_POST_PAGES; page += 1) {
            const data = await apiRequest("/marketing/posts", { query: { projectId, limit: 100, cursor }, token });
            posts.push(...(data?.items || []));
            cursor = data?.nextCursor;
            if (!cursor) break;
        }
        const unassigned = await apiRequest("/marketing/unassigned-posts", { query: { projectId }, token });

        const rows = [
            ...posts.flatMap((post) =>
                (post.platforms || []).map((publication) => ({
                    ...publication,
                    key: `${post._id}-${publication._id}`,
                    postId: post._id,
                    postTitle: post.title,
                    postType: post.type,
                    postStatus: post.status,
                    unassigned: false,
                }))
            ),
            ...(unassigned || []).map((publication) => ({
                ...publication,
                key: `unassigned-${publication._id}`,
                postId: null,
                postTitle: publication.title || publication.copy || "Sin título",
                unassigned: true,
            })),
        ];
        return { projectId, rows, truncated: Boolean(cursor) };
    })
);

export const fetchPostDetail = createAsyncThunk(
    "marketing/fetchPostDetail",
    withApi(({ id }, { token }) => apiRequest(`/marketing/posts/${id}`, { token }))
);

export const updatePublicationMetrics = createAsyncThunk(
    "marketing/updatePublicationMetrics",
    withApi(({ row, changes }, { token }) =>
        apiRequest(
            row.unassigned ? `/marketing/unassigned-posts/${row._id}` : `/marketing/posts/${row.postId}/platforms/${row._id}`,
            { method: "PATCH", body: changes, token }
        )
    )
);

// Consulta en la red social las métricas actuales de una publicación.
export const refreshPublication = createAsyncThunk(
    "marketing/refreshPublication",
    withApi(({ row }, { token }) =>
        apiRequest(
            row.unassigned
                ? `/marketing/unassigned-posts/${row._id}/refresh`
                : `/marketing/posts/${row.postId}/platforms/${row._id}/refresh`,
            { method: "POST", token }
        )
    )
);

export const refreshProjectMetrics = createAsyncThunk(
    "marketing/refreshProjectMetrics",
    withApi(({ projectId }, { token }) =>
        apiRequest(`/marketing/projects/${projectId}/refresh-current`, { method: "POST", token })
    )
);

// Descarta una publicación sin asignar (p. ej. un video borrado): deja de
// contarse y de medirse, y no se vuelve a crear.
export const discardPublication = createAsyncThunk(
    "marketing/discardPublication",
    withApi(async ({ row }, { token }) => {
        await apiRequest(`/marketing/unassigned-posts/${row._id}`, { method: "DELETE", token });
        return row.key;
    })
);

// ── Analítica (dashboards) ───────────────────────────────────────────────────

const analyticsThunk = (key, path) =>
    createAsyncThunk(
        `marketing/fetch_${key}`,
        withApi(({ projectId, ...query }, { token }) =>
            apiRequest(`/marketing/projects/${projectId}/${path}`, { query, token })
        )
    );

export const fetchSummary = analyticsThunk("summary", "summary");
export const fetchGrowth = analyticsThunk("growth", "growth");
export const fetchPostImpact = analyticsThunk("impact", "post-impact");
export const fetchLag = analyticsThunk("lag", "lag");
export const fetchLiveGames = analyticsThunk("live", "roblox-games/live");

const emptyRequest = { data: null, status: "idle", error: null };

const analytics = {
    summary: fetchSummary,
    growth: fetchGrowth,
    impact: fetchPostImpact,
    lag: fetchLag,
    live: fetchLiveGames,
    publications: fetchPublications,
};

// Actualiza las filas de un post editado sin volver a cargar todas las publicaciones.
const mergePost = (state, post) => {
    const rows = state.publications.data?.rows;
    if (!rows || !post) return;
    (post.platforms || []).forEach((publication) => {
        const row = rows.find((item) => item.postId === post._id && item._id === publication._id);
        if (row) Object.assign(row, publication);
    });
};

// Las acciones sobre una publicación devuelven el post completo, o la
// publicación si está sin asignar.
const mergePublication = (state, action) => {
    if (action.meta.arg.row.unassigned) {
        const row = state.publications.data?.rows.find((item) => item.unassigned && item._id === action.payload?._id);
        if (row) Object.assign(row, action.payload);
    } else {
        mergePost(state, action.payload);
    }
};

// ── Slice ────────────────────────────────────────────────────────────────────

const marketingSlice = createSlice({
    name: "marketing",
    initialState: {
        projects: [],
        projectsStatus: "idle",
        projectsError: null,
        platforms: [],
        apiHealth: null,
        robloxGames: {},
        ...Object.fromEntries(Object.keys(analytics).map((key) => [key, emptyRequest])),
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchProjects.pending, (state) => {
                state.projectsStatus = "loading";
                state.projectsError = null;
            })
            .addCase(fetchProjects.fulfilled, (state, action) => {
                state.projectsStatus = "succeeded";
                state.projects = action.payload || [];
            })
            .addCase(fetchProjects.rejected, (state, action) => {
                state.projectsStatus = "failed";
                state.projectsError = action.payload || "Error al cargar proyectos";
            })
            .addCase(deleteProject.fulfilled, (state, action) => {
                state.projects = state.projects.filter((project) => project._id !== action.payload);
            })
            .addCase(fetchPlatforms.fulfilled, (state, action) => {
                state.platforms = action.payload || [];
            })
            .addCase(fetchApiHealth.fulfilled, (state, action) => {
                state.apiHealth = action.payload;
            })
            .addCase(fetchApiHealth.rejected, (state) => {
                state.apiHealth = { status: "down", database: "unknown" };
            })
            .addCase(updatePublicationMetrics.fulfilled, mergePublication)
            .addCase(discardPublication.fulfilled, (state, action) => {
                if (!state.publications.data) return;
                state.publications.data.rows = state.publications.data.rows.filter((row) => row.key !== action.payload);
            })
            .addCase(refreshPublication.fulfilled, mergePublication)
            .addCase(fetchRobloxGames.fulfilled, (state, action) => {
                state.robloxGames[action.payload.projectId] = action.payload.games;
            });

        // Solo se guarda la respuesta de la última petición, para que cambiar de
        // proyecto o de fechas rápido no deje datos de una consulta anterior.
        Object.entries(analytics).forEach(([key, thunk]) => {
            builder
                .addCase(thunk.pending, (state, action) => {
                    state[key] = { ...state[key], status: "loading", error: null, requestId: action.meta.requestId };
                })
                .addCase(thunk.fulfilled, (state, action) => {
                    if (state[key].requestId !== action.meta.requestId) return;
                    state[key] = { data: action.payload, status: "succeeded", error: null };
                })
                .addCase(thunk.rejected, (state, action) => {
                    if (state[key].requestId !== action.meta.requestId) return;
                    state[key] = { data: null, status: "failed", error: action.payload || "Error al cargar datos" };
                });
        });
    },
});

export default marketingSlice.reducer;
